import {
  type Dropdown,
  type LeadsStatusesListData,
  type LeadsTypesListData,
  type PaginatedDropdownList,
  type SequencesListData,
  type SequencesStepsListData,
  type SourcesListListData,
  type TagsListData,
  type UsersListListData,
  leadsStatusesListListInfiniteOptions,
  leadsTypesListListInfiniteOptions,
  leadsTypesListListOptions,
  sequencesListListInfiniteOptions,
  sequencesStepsListListInfiniteOptions,
  sourcesListListInfiniteOptions,
  tagsListListInfiniteOptions,
  usersListListInfiniteOptions
} from '@crm/api'
import { RangePickerFilter } from '@crm/components'
import { InfiniteSelect } from '@crm/components'
import { useAppLocation } from '@crm/hooks/useAppLocation'
import { useRolePermission } from '@crm/hooks/useRolePermission'
import { UserRoleCRM } from '@crm/types/userRoleCrm'
import {
  defaultFiltersMultipleValues,
  defaultFiltersNumberValues
} from '@crm/utils'
import { MoreFiltersPopover } from '@shared/components'
import { useQueryUpdate } from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { defaultFilters } from '@shared/utils'
import { useQuery } from '@tanstack/react-query'
import { Tooltip } from 'antd'
import { useEffect } from 'react'
import { useParams } from 'react-router-dom'

import type { LeadsModalType } from '@crm/types'

interface Props {
  defaultLeadType?: boolean
  setModalType?: (value: LeadsModalType) => void
  setSelectedId?: (value: number) => void
}

export const LeadsFilters = ({ defaultLeadType }: Props) => {
  const { t } = useLocales()
  const location = useAppLocation()

  const { id } = useParams()
  const { params, updateQuery } = useQueryUpdate()

  const { data: leadTypesData } = useQuery(
    leadsTypesListListOptions({ query: { per_page: 1 } })
  )

  const hasPermissionMix = useRolePermission([
    UserRoleCRM.manager,
    UserRoleCRM.admin
  ])

  useEffect(() => {
    if (
      defaultLeadType &&
      !params?.lead_type &&
      !id &&
      leadTypesData?.results?.[0]
    ) {
      updateQuery({
        lead_type: String(leadTypesData?.results?.[0]?.key),
        page: defaultFilters.page
      })
    }
  }, [defaultLeadType, params?.lead_type, id, leadTypesData, updateQuery])

  const filtersCount = [
    'tags',
    'sequence',
    'step',
    'source',
    'owner',
    'created_at_from'
  ].filter((filter) => params?.[filter])?.length

  const isListView = location.pathname.includes('list-view')

  const leadsStatusValue = defaultFiltersMultipleValues(params?.status, true)
  const sourceValue = defaultFiltersMultipleValues(params?.source, true)
  const leadTypeValue = defaultFiltersMultipleValues(params?.lead_type, true)
  const tagsValue = defaultFiltersMultipleValues(params?.tags, true)
  const ownerValue = defaultFiltersMultipleValues(params?.owner)
  const campaignValue = defaultFiltersNumberValues(params?.sequence)
  const campaignStepValue = defaultFiltersNumberValues(params?.step)

  const leadTypeId = id && !isListView ? Number(id) : params?.lead_type

  const onFilterChange = (filter: string, value: string[]) => {
    updateQuery({ [filter]: value, page: defaultFilters.page })
  }

  const onResetMoreFilters = () =>
    updateQuery({
      source: undefined,
      created_at_from: undefined,
      created_at_to: undefined,
      tags: undefined,
      owner: undefined,
      sequence: undefined,
      step: undefined
    })

  return (
    <>
      {((!id && !isListView) || (id && isListView)) && (
        <InfiniteSelect<LeadsTypesListData, PaginatedDropdownList, Dropdown>
          className="select-min-width"
          placeholder={t('contacts.pipeline')}
          getQueryFunction={leadsTypesListListInfiniteOptions}
          onChange={(leadType) => onFilterChange('lead_type', leadType)}
          baseOptions={{
            query: {
              preference: params?.lead_type
            }
          }}
          value={leadTypeValue}
        />
      )}

      <InfiniteSelect<LeadsStatusesListData, PaginatedDropdownList, Dropdown>
        placeholder={t('filter.status')}
        className="select-min-width"
        getQueryFunction={leadsStatusesListListInfiniteOptions}
        onChange={(status) => onFilterChange('status', status)}
        baseOptions={{
          query: {
            preference: params?.status,
            lead_type: leadTypeId
          }
        }}
        value={leadsStatusValue}
        mode="multiple"
        maxTagCount={1}
        maxTagTextLength={6}
      />

      <MoreFiltersPopover
        filterActiveCount={filtersCount}
        onResetMoreFilters={onResetMoreFilters}
      >
        <InfiniteSelect<SourcesListListData, PaginatedDropdownList, Dropdown>
          placeholder={t('contacts.source')}
          getQueryFunction={sourcesListListInfiniteOptions}
          onChange={(source) => onFilterChange('source', source)}
          baseOptions={{
            query: {
              preference: params?.source
            }
          }}
          value={sourceValue}
          mode="multiple"
          maxTagCount={1}
          maxTagTextLength={6}
        />

        <Tooltip title={`${t('created_at_from')} - ${t('created_at_to')}`}>
          <RangePickerFilter
            fromParam="created_at_from"
            toParam="created_at_to"
            placeholder={[t('created_at_from'), t('created_at_to')]}
          />
        </Tooltip>

        <InfiniteSelect<TagsListData, PaginatedDropdownList, Dropdown>
          placeholder={t('sales_funnel_report.tags')}
          getQueryFunction={tagsListListInfiniteOptions}
          onChange={(tags) => onFilterChange('tags', tags)}
          baseOptions={{
            query: {
              preference: params?.tags
            }
          }}
          value={tagsValue}
          mode="multiple"
          className="w-full"
          maxTagCount={1}
          maxTagTextLength={6}
        />

        {hasPermissionMix && (
          <InfiniteSelect<UsersListListData, PaginatedDropdownList, Dropdown>
            value={ownerValue}
            className="select-min-width"
            popupMatchSelectWidth
            placeholder={t('opportunities.responsible')}
            getQueryFunction={usersListListInfiniteOptions}
            onChange={(owner) => onFilterChange('owner', owner)}
            baseOptions={{
              query: {
                preference: params?.owner
              }
            }}
            mode="multiple"
            maxTagCount={1}
            maxTagTextLength={6}
          />
        )}

        <InfiniteSelect<SequencesListData, PaginatedDropdownList, Dropdown>
          value={campaignValue}
          className="w-full"
          popupMatchSelectWidth
          placeholder={t('lead.campaigns')}
          getQueryFunction={sequencesListListInfiniteOptions}
          onChange={(sequence) => onFilterChange('sequence', sequence)}
          baseOptions={{
            query: {
              preference: params?.sequence
            }
          }}
        />

        <InfiniteSelect<SequencesStepsListData, PaginatedDropdownList, Dropdown>
          value={campaignStepValue}
          className="w-full"
          popupMatchSelectWidth
          placeholder={t('campaigns.steps')}
          getQueryFunction={sequencesStepsListListInfiniteOptions}
          onChange={(step) => onFilterChange('step', step)}
          baseOptions={{
            path: {
              sequence_id: String(campaignValue)
            },
            query: {
              preference: params?.step
            }
          }}
          disabled={!campaignValue}
        />
      </MoreFiltersPopover>
    </>
  )
}
